import {
  CampanhaSimulada,
  ProdutoServicoSimulador,
  ParticipanteSimulador,
  MetaGerada,
  DistintivoGerado,
  MissaoGerada,
  NivelProgressao,
  RecompensaSelecionada,
  NivelCampanha,
  TipoCampanha
} from '../types/simulador';
import {
  obterIncentivoPorPesoPerfil,
  calcularPontuacao,
  obterAtratividade
} from './matrizPesoPerfil';
import { analisarCampanhaCompleta, adicionarCampanhaAoHistorico } from './campanhaAnalyzer';

const NIVEIS_FIXOS: NivelCampanha[] = ['Bronze', 'Prata', 'Ouro', 'Platina', 'Diamante'];

// NOVA: Configuração de IA
export interface ConfiguracaoIA {
  quantidadeMetas?: number;
  quantidadeDistintivos?: number;
  quantidadeNiveis?: number;
  periodoMissoes?: 'completo' | 'diarias' | 'semanais' | 'diarias-semanais';
  missoesDiarias?: number;
  missoesSemanais?: number;
  missoesMensais?: number;
  dificuldade?: 'facil' | 'equilibrada' | 'desafiadora';
  focoRecompensas?: 'pontos' | 'xp' | 'balanceado';
}

// NOVA: Aplicar dificuldade às metas
const aplicarDificuldade = (valor: number, dificuldade: 'facil' | 'equilibrada' | 'desafiadora'): number => {
  switch (dificuldade) {
    case 'facil':
      return Math.round(valor * 0.7); // 30% mais fácil
    case 'desafiadora':
      return Math.round(valor * 1.3); // 30% mais difícil
    default:
      return valor;
  }
};

// NOVA: Aplicar foco de recompensas
const aplicarFocoRecompensas = (
  pontos: number,
  focoRecompensas: 'pontos' | 'xp' | 'balanceado'
): { pontos: number; xp: number } => {
  switch (focoRecompensas) {
    case 'pontos':
      return { pontos: Math.round(pontos * 1.5), xp: Math.round(pontos * 0.3) };
    case 'xp':
      return { pontos: Math.round(pontos * 0.5), xp: Math.round(pontos * 1.2) };
    default:
      return { pontos, xp: Math.round(pontos * 0.5) };
  }
};

export const gerarMetasAutomaticas = (
  produtos: ProdutoServicoSimulador[],
  tipo: TipoCampanha,
  config?: ConfiguracaoIA
): MetaGerada[] => {
  const metas: MetaGerada[] = [];
  let idCounter = 1;
  
  const dificuldade = config?.dificuldade || 'equilibrada';
  const focoRecompensas = config?.focoRecompensas || 'balanceado';
  const quantidadeDesejada = config?.quantidadeMetas || 5;

  // Metas específicas para cada produto/serviço
  produtos.forEach((produto) => {
    const incentivo = obterIncentivoPorPesoPerfil(produto.pesoComercial, produto.perfilEconomico);
    
    // Meta de volume por nível para CADA produto (em PONTOS)
    NIVEIS_FIXOS.forEach((nivel, index) => {
      if (metas.length >= quantidadeDesejada) return;
      
      const multiplicador = index + 1;
      const volumeBase = 3 * multiplicador;
      const volumeMeta = aplicarDificuldade(volumeBase, dificuldade);
      
      const recompensas = aplicarFocoRecompensas(incentivo.pontosMax * multiplicador, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Vender ${volumeMeta} unidades de ${produto.descricao}`,
        tipo: 'volume',
        valorMeta: volumeMeta,
        pontos: recompensas.pontos,
        nivel
      });
    });

    // Meta de valor específica do produto (em PONTOS)
    if (produto.valorReferencia && metas.length < quantidadeDesejada) {
      const valorBase = produto.valorReferencia * 5;
      const valorMeta = aplicarDificuldade(valorBase, dificuldade);
      const recompensas = aplicarFocoRecompensas(incentivo.pontosMax * 2, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Atingir R$ ${valorMeta.toLocaleString('pt-BR')} em vendas de ${produto.descricao}`,
        tipo: 'valor',
        valorMeta: valorMeta,
        pontos: recompensas.pontos,
        nivel: 'Prata'
      });
    }

    // Meta de primeira venda do produto (em PONTOS)
    if (metas.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(Math.round(incentivo.pontosMin * 0.5), focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Realizar a primeira venda de ${produto.descricao}`,
        tipo: 'frequencia',
        valorMeta: 1,
        pontos: recompensas.pontos,
        nivel: 'Bronze'
      });
    }
  });

  // Metas gerais combinadas quando há múltiplos produtos
  if (produtos.length > 1 && metas.length < quantidadeDesejada) {
    // Meta de diversificação
    const diversificacaoBase = Math.min(3, produtos.length);
    const diversificacaoMeta = aplicarDificuldade(diversificacaoBase, dificuldade);
    const recompensas1 = aplicarFocoRecompensas(800, focoRecompensas);
    
    metas.push({
      id: `meta-${idCounter++}`,
      descricao: `Vender pelo menos ${diversificacaoMeta} produtos diferentes`,
      tipo: 'frequencia',
      valorMeta: diversificacaoMeta,
      pontos: recompensas1.pontos,
      nivel: 'Prata'
    });

    if (metas.length < quantidadeDesejada) {
      const recompensas2 = aplicarFocoRecompensas(2000, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Vender todos os ${produtos.length} produtos da campanha`,
        tipo: 'frequencia',
        valorMeta: produtos.length,
        pontos: recompensas2.pontos,
        nivel: 'Ouro'
      });
    }

    // Meta de volume total
    if (metas.length < quantidadeDesejada) {
      const volumeBase1 = 20;
      const volumeMeta1 = aplicarDificuldade(volumeBase1, dificuldade);
      const recompensas3 = aplicarFocoRecompensas(1000, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Realizar ${volumeMeta1} vendas no total (qualquer produto)`,
        tipo: 'frequencia',
        valorMeta: volumeMeta1,
        pontos: recompensas3.pontos,
        nivel: 'Prata'
      });
    }

    if (metas.length < quantidadeDesejada) {
      const volumeBase2 = 50;
      const volumeMeta2 = aplicarDificuldade(volumeBase2, dificuldade);
      const recompensas4 = aplicarFocoRecompensas(3000, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Realizar ${volumeMeta2} vendas no total (qualquer produto)`,
        tipo: 'frequencia',
        valorMeta: volumeMeta2,
        pontos: recompensas4.pontos,
        nivel: 'Ouro'
      });
    }

    // Meta de produtos estratégicos
    const produtosEstrategicos = produtos.filter(p => p.pesoComercial === 'Estratégico');
    if (produtosEstrategicos.length > 0 && metas.length < quantidadeDesejada) {
      const volumeEstrategico = aplicarDificuldade(5, dificuldade);
      const recompensas5 = aplicarFocoRecompensas(2500, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Vender ${volumeEstrategico} unidades de produtos estratégicos`,
        tipo: 'volume',
        valorMeta: volumeEstrategico,
        pontos: recompensas5.pontos,
        nivel: 'Platina'
      });
    }

    // Meta de produtos de alto valor
    const produtosAltos = produtos.filter(p => p.pesoComercial === 'Alto');
    if (produtosAltos.length > 0 && metas.length < quantidadeDesejada) {
      const volumeAlto = aplicarDificuldade(10, dificuldade);
      const recompensas6 = aplicarFocoRecompensas(1800, focoRecompensas);
      
      metas.push({
        id: `meta-${idCounter++}`,
        descricao: `Vender ${volumeAlto} unidades de produtos de alto valor`,
        tipo: 'volume',
        valorMeta: volumeAlto,
        pontos: recompensas6.pontos,
        nivel: 'Ouro'
      });
    }
  } else if (metas.length < quantidadeDesejada) {
    // Metas gerais para campanha com único produto
    const volumes = [
      { base: 10, nivel: 'Bronze' as NivelCampanha, pontos: 500 },
      { base: 25, nivel: 'Prata' as NivelCampanha, pontos: 1500 },
      { base: 50, nivel: 'Ouro' as NivelCampanha, pontos: 3500 }
    ];

    volumes.forEach(v => {
      if (metas.length < quantidadeDesejada) {
        const volumeMeta = aplicarDificuldade(v.base, dificuldade);
        const recompensas = aplicarFocoRecompensas(v.pontos, focoRecompensas);
        
        metas.push({
          id: `meta-${idCounter++}`,
          descricao: `Realizar ${volumeMeta} vendas no mês`,
          tipo: 'frequencia',
          valorMeta: volumeMeta,
          pontos: recompensas.pontos,
          nivel: v.nivel
        });
      }
    });
  }

  // Limitar ao número desejado
  return metas.slice(0, quantidadeDesejada);
};

export const gerarDistintivosAutomaticos = (
  produtos: ProdutoServicoSimulador[],
  config?: ConfiguracaoIA
): DistintivoGerado[] => {
  const distintivos: DistintivoGerado[] = [];
  let idCounter = 1;
  
  const quantidadeDesejada = config?.quantidadeDistintivos || 6;
  const focoRecompensas = config?.focoRecompensas || 'balanceado';

  // Distintivos específicos por produto
  produtos.forEach((produto) => {
    if (distintivos.length >= quantidadeDesejada) return;
    
    const incentivo = obterIncentivoPorPesoPerfil(produto.pesoComercial, produto.perfilEconomico);
    const recompensas = aplicarFocoRecompensas(Math.round(incentivo.pontosMax * 0.8), focoRecompensas);
    
    // Distintivo de especialista no produto
    distintivos.push({
      id: `dist-${idCounter++}`,
      nome: `Especialista em ${produto.descricao}`,
      descricao: `Vendeu 10 unidades de ${produto.descricao}`,
      icone: 'ri-medal-fill',
      condicao: `Vender 10 unidades de ${produto.descricao}`,
      pontos: recompensas.xp
    });

    // Distintivo de mestre no produto
    if ((produto.pesoComercial === 'Estratégico' || produto.pesoComercial === 'Alto') && distintivos.length < quantidadeDesejada) {
      const recompensas2 = aplicarFocoRecompensas(Math.round(incentivo.pontosMax * 1.5), focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: `Mestre ${produto.descricao}`,
        descricao: `Vendeu 25 unidades de ${produto.descricao}`,
        icone: 'ri-vip-crown-fill',
        condicao: `Vender 25 unidades de ${produto.descricao}`,
        pontos: recompensas2.xp
      });
    }
  });

  // Distintivos por peso comercial (quando há múltiplos produtos)
  if (produtos.length > 1) {
    const produtosEstrategicos = produtos.filter(p => p.pesoComercial === 'Estratégico');
    if (produtosEstrategicos.length > 0 && distintivos.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(1500, focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: 'Especialista Estratégico',
        descricao: `Vendeu 5 produtos estratégicos diferentes`,
        icone: 'ri-star-fill',
        condicao: 'Vender 5 produtos estratégicos',
        pontos: recompensas.xp
      });
    }

    const produtosAltos = produtos.filter(p => p.pesoComercial === 'Alto');
    if (produtosAltos.length > 0 && distintivos.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(1000, focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: 'Vendedor Premium',
        descricao: 'Vendeu 10 produtos de alto valor',
        icone: 'ri-vip-crown-fill',
        condicao: 'Vender 10 produtos de peso Alto',
        pontos: recompensas.xp
      });
    }

    // Distintivo de diversificação
    if (distintivos.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(2000, focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: 'Vendedor Versátil',
        descricao: `Vendeu todos os ${produtos.length} produtos da campanha`,
        icone: 'ri-checkbox-multiple-fill',
        condicao: 'Vender todos os produtos',
        pontos: recompensas.xp
      });
    }

    // Distintivo de mix de produtos
    if (produtos.length >= 3 && distintivos.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(800, focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: 'Mix Perfeito',
        descricao: 'Vendeu pelo menos 3 produtos diferentes no mesmo dia',
        icone: 'ri-stack-fill',
        condicao: 'Vender 3 produtos diferentes em 1 dia',
        pontos: recompensas.xp
      });
    }
  }

  // Distintivos gerais (em XP)
  const distintivosGerais = [
    { nome: 'Primeiro Passo', descricao: 'Realizou a primeira venda na campanha', icone: 'ri-footprint-fill', condicao: 'Primeira venda', pontos: 100 },
    { nome: 'Vendedor Consistente', descricao: 'Vendeu por 7 dias consecutivos', icone: 'ri-calendar-check-fill', condicao: '7 dias de vendas consecutivas', pontos: 500 },
    { nome: 'Maratonista', descricao: 'Vendeu por 30 dias consecutivos', icone: 'ri-run-fill', condicao: '30 dias de vendas consecutivas', pontos: 1500 },
    { nome: 'Top Performer', descricao: 'Ficou entre os 3 melhores do mês', icone: 'ri-trophy-fill', condicao: 'Top 3 do ranking mensal', pontos: 2000 },
    { nome: 'Mestre das Vendas', descricao: 'Atingiu todas as metas do mês', icone: 'ri-medal-fill', condicao: '100% das metas cumpridas', pontos: 3000 },
    { nome: 'Velocista', descricao: 'Realizou 10 vendas em um único dia', icone: 'ri-flashlight-fill', condicao: '10 vendas em 1 dia', pontos: 800 },
    { nome: 'Campeão da Semana', descricao: 'Foi o melhor vendedor da semana', icone: 'ri-award-fill', condicao: 'Melhor da semana', pontos: 1200 }
  ];

  distintivosGerais.forEach(d => {
    if (distintivos.length < quantidadeDesejada) {
      const recompensas = aplicarFocoRecompensas(d.pontos, focoRecompensas);
      
      distintivos.push({
        id: `dist-${idCounter++}`,
        nome: d.nome,
        descricao: d.descricao,
        icone: d.icone,
        condicao: d.condicao,
        pontos: recompensas.xp
      });
    }
  });

  return distintivos.slice(0, quantidadeDesejada);
};

export const gerarMissoesAutomaticas = (
  produtos: ProdutoServicoSimulador[],
  config?: ConfiguracaoIA
): MissaoGerada[] => {
  const missoes: MissaoGerada[] = [];
  let idCounter = 1;
  
  const periodoMissoes = config?.periodoMissoes || 'completo';
  const missoesDiarias = config?.missoesDiarias || 3;
  const missoesSemanais = config?.missoesSemanais || 3;
  const missoesMensais = config?.missoesMensais || 2;
  const focoRecompensas = config?.focoRecompensas || 'balanceado';
  const dificuldade = config?.dificuldade || 'equilibrada';

  // Missões específicas por produto (quando há múltiplos)
  if (produtos.length > 1) {
    // Missão diária de diversificação
    if ((periodoMissoes === 'completo' || periodoMissoes === 'diarias' || periodoMissoes === 'diarias-semanais') && missoes.filter(m => m.tipo === 'diaria').length < missoesDiarias) {
      const recompensas = aplicarFocoRecompensas(100, focoRecompensas);
      
      missoes.push({
        id: `missao-${idCounter++}`,
        titulo: 'Diversificação Diária',
        descricao: 'Venda 2 produtos diferentes hoje',
        tipo: 'diaria',
        objetivo: 'Vender 2 produtos diferentes',
        recompensa: recompensas.xp,
        nivel: 'Bronze'
      });
    }

    // Missão semanal de mix
    if ((periodoMissoes === 'completo' || periodoMissoes === 'semanais' || periodoMissoes === 'diarias-semanais') && missoes.filter(m => m.tipo === 'semanal').length < missoesSemanais) {
      const volumeMix = aplicarDificuldade(Math.min(3, produtos.length), dificuldade);
      const recompensas = aplicarFocoRecompensas(600, focoRecompensas);
      
      missoes.push({
        id: `missao-${idCounter++}`,
        titulo: 'Mix Completo',
        descricao: `Venda pelo menos ${volumeMix} produtos diferentes esta semana`,
        tipo: 'semanal',
        objetivo: `Vender ${volumeMix} produtos diferentes`,
        recompensa: recompensas.xp,
        nivel: 'Prata'
      });
    }

    // Missões para produtos estratégicos
    const produtosEstrategicos = produtos.filter(p => p.pesoComercial === 'Estratégico');
    if (produtosEstrategicos.length > 0 && (periodoMissoes === 'completo' || periodoMissoes === 'semanais' || periodoMissoes === 'diarias-semanais') && missoes.filter(m => m.tipo === 'semanal').length < missoesSemanais) {
      const volumeEstrategico = aplicarDificuldade(2, dificuldade);
      const recompensas = aplicarFocoRecompensas(800, focoRecompensas);
      
      missoes.push({
        id: `missao-${idCounter++}`,
        titulo: 'Foco Estratégico',
        descricao: `Venda ${volumeEstrategico} produtos estratégicos esta semana`,
        tipo: 'semanal',
        objetivo: `Vender ${volumeEstrategico} produtos estratégicos`,
        recompensa: recompensas.xp,
        nivel: 'Ouro'
      });
    }

    // Missão mensal de todos os produtos
    if ((periodoMissoes === 'completo') && missoes.filter(m => m.tipo === 'mensal').length < missoesMensais) {
      const recompensas = aplicarFocoRecompensas(3000, focoRecompensas);
      
      missoes.push({
        id: `missao-${idCounter++}`,
        titulo: 'Portfólio Completo',
        descricao: `Venda todos os ${produtos.length} produtos este mês`,
        tipo: 'mensal',
        objetivo: `Vender todos os ${produtos.length} produtos`,
        recompensa: recompensas.xp,
        nivel: 'Diamante'
      });
    }
  }

  // Missões diárias gerais (em XP)
  if (periodoMissoes === 'completo' || periodoMissoes === 'diarias' || periodoMissoes === 'diarias-semanais') {
    const missoesDiariasGerais = [
      { titulo: 'Venda do Dia', descricao: 'Realize pelo menos 1 venda hoje', objetivo: 'Vender 1 produto', pontos: 50, nivel: 'Bronze' as NivelCampanha },
      { titulo: 'Trio Vencedor', descricao: 'Realize 3 vendas hoje', objetivo: 'Vender 3 produtos', pontos: 150, nivel: 'Prata' as NivelCampanha },
      { titulo: 'Dia Produtivo', descricao: 'Realize 5 vendas hoje', objetivo: 'Vender 5 produtos', pontos: 300, nivel: 'Ouro' as NivelCampanha }
    ];

    missoesDiariasGerais.forEach(m => {
      if (missoes.filter(ms => ms.tipo === 'diaria').length < missoesDiarias) {
        const volumeAjustado = aplicarDificuldade(parseInt(m.objetivo.match(/\d+/)?.[0] || '1'), dificuldade);
        const recompensas = aplicarFocoRecompensas(m.pontos, focoRecompensas);
        
        missoes.push({
          id: `missao-${idCounter++}`,
          titulo: m.titulo,
          descricao: m.descricao.replace(/\d+/, volumeAjustado.toString()),
          tipo: 'diaria',
          objetivo: m.objetivo.replace(/\d+/, volumeAjustado.toString()),
          recompensa: recompensas.xp,
          nivel: m.nivel
        });
      }
    });
  }

  // Missões semanais gerais (em XP)
  if (periodoMissoes === 'completo' || periodoMissoes === 'semanais' || periodoMissoes === 'diarias-semanais') {
    const missoesSemanaisGerais = [
      { titulo: 'Semana Produtiva', descricao: 'Realize 10 vendas esta semana', objetivo: 'Vender 10 produtos', pontos: 500, nivel: 'Ouro' as NivelCampanha },
      { titulo: 'Semana Intensa', descricao: 'Realize 20 vendas esta semana', objetivo: 'Vender 20 produtos', pontos: 1000, nivel: 'Platina' as NivelCampanha },
      { titulo: 'Consistência Semanal', descricao: 'Venda todos os dias da semana', objetivo: 'Vender em 7 dias consecutivos', pontos: 700, nivel: 'Ouro' as NivelCampanha }
    ];

    missoesSemanaisGerais.forEach(m => {
      if (missoes.filter(ms => ms.tipo === 'semanal').length < missoesSemanais) {
        const volumeMatch = m.objetivo.match(/\d+/);
        const volumeAjustado = volumeMatch ? aplicarDificuldade(parseInt(volumeMatch[0]), dificuldade) : null;
        const recompensas = aplicarFocoRecompensas(m.pontos, focoRecompensas);
        
        missoes.push({
          id: `missao-${idCounter++}`,
          titulo: m.titulo,
          descricao: volumeAjustado ? m.descricao.replace(/\d+/, volumeAjustado.toString()) : m.descricao,
          tipo: 'semanal',
          objetivo: volumeAjustado ? m.objetivo.replace(/\d+/, volumeAjustado.toString()) : m.objetivo,
          recompensa: recompensas.xp,
          nivel: m.nivel
        });
      }
    });
  }

  // Missões mensais gerais (em XP)
  if (periodoMissoes === 'completo') {
    const missoesMensaisGerais = [
      { titulo: 'Campeão do Mês', descricao: 'Realize 50 vendas este mês', objetivo: 'Vender 50 produtos', pontos: 2000, nivel: 'Platina' as NivelCampanha },
      { titulo: 'Superação Total', descricao: 'Realize 100 vendas este mês', objetivo: 'Vender 100 produtos', pontos: 5000, nivel: 'Diamante' as NivelCampanha },
      { titulo: 'Mês Perfeito', descricao: 'Atinja todas as metas mensais', objetivo: 'Cumprir 100% das metas', pontos: 4000, nivel: 'Diamante' as NivelCampanha }
    ];

    missoesMensaisGerais.forEach(m => {
      if (missoes.filter(ms => ms.tipo === 'mensal').length < missoesMensais) {
        const volumeMatch = m.objetivo.match(/\d+/);
        const volumeAjustado = volumeMatch ? aplicarDificuldade(parseInt(volumeMatch[0]), dificuldade) : null;
        const recompensas = aplicarFocoRecompensas(m.pontos, focoRecompensas);
        
        missoes.push({
          id: `missao-${idCounter++}`,
          titulo: m.titulo,
          descricao: volumeAjustado ? m.descricao.replace(/\d+/, volumeAjustado.toString()) : m.descricao,
          tipo: 'mensal',
          objetivo: volumeAjustado ? m.objetivo.replace(/\d+/, volumeAjustado.toString()) : m.objetivo,
          recompensa: recompensas.xp,
          nivel: m.nivel
        });
      }
    });
  }

  return missoes;
};

export const gerarNiveisProgressao = (
  produtos: ProdutoServicoSimulador[],
  config?: ConfiguracaoIA
): NivelProgressao[] => {
  const niveis: NivelProgressao[] = [];
  
  const quantidadeNiveis = config?.quantidadeNiveis || 10;
  const focoRecompensas = config?.focoRecompensas || 'balanceado';
  
  const xpBase = produtos.reduce((acc, p) => {
    const incentivo = obterIncentivoPorPesoPerfil(p.pesoComercial, p.perfilEconomico);
    return acc + incentivo.pontosMax;
  }, 0);

  // Gera níveis dinamicamente baseado na quantidade configurada
  const niveisFixos = NIVEIS_FIXOS;
  const multiplicadorBase = quantidadeNiveis / 5; // Ajusta multiplicador baseado na quantidade

  for (let i = 0; i < Math.min(quantidadeNiveis, 5); i++) {
    const nivel = niveisFixos[i];
    const multiplicador = (i + 1) * multiplicadorBase;
    
    const pontosMin = i === 0 ? 0 : Math.round(xpBase * ((i) * multiplicadorBase));
    const pontosMax = Math.round(xpBase * multiplicador);

    const beneficios = [
      i === 0 ? 'Acesso ao catálogo básico' : `Acesso ao catálogo ${nivel.toLowerCase()}`,
      `Distintivos ${nivel}`,
      i === 0 ? 'Missões diárias' : i === 1 ? 'Missões semanais' : 'Missões mensais'
    ];

    if (i > 0) {
      beneficios.push(`Bônus de ${i * 10}% em XP`);
    }

    if (i === 4) {
      beneficios.push('Prioridade no suporte');
    }

    niveis.push({
      nivel,
      pontosMinimos: pontosMin,
      pontosMaximos: pontosMax,
      recompensas: [],
      beneficios
    });
  }

  return niveis;
};

export const selecionarRecompensasAutomaticas = (
  produtos: ProdutoServicoSimulador[],
  niveis: NivelProgressao[]
): RecompensaSelecionada[] => {
  const recompensas: RecompensaSelecionada[] = [];
  let idCounter = 1;

  // Recompensas por nível baseadas na atratividade média
  const atratividadeMedia = produtos.reduce((acc, p) => {
    return acc + obterAtratividade(p.pesoComercial, p.perfilEconomico);
  }, 0) / produtos.length;

  const recompensasPorNivel: Record<NivelCampanha, Array<{ nome: string; tipo: any; descricao: string; custo: number }>> = {
    Bronze: [
      { nome: 'Vale-Compras R$ 50', tipo: 'voucher', descricao: 'Vale-compras de R$ 50 em lojas parceiras', custo: 50 },
      { nome: 'Certificado de Reconhecimento', tipo: 'experiencia', descricao: 'Certificado digital de reconhecimento', custo: 0 }
    ],
    Prata: [
      { nome: 'Vale-Compras R$ 100', tipo: 'voucher', descricao: 'Vale-compras de R$ 100 em lojas parceiras', custo: 100 },
      { nome: 'Gift Card R$ 150', tipo: 'giftcard', descricao: 'Gift card de R$ 150 para uso livre', custo: 150 }
    ],
    Ouro: [
      { nome: 'Jantar para 2 Pessoas', tipo: 'experiencia', descricao: 'Jantar em restaurante premium para 2 pessoas', custo: 300 },
      { nome: 'Vale-Compras R$ 250', tipo: 'voucher', descricao: 'Vale-compras de R$ 250 em lojas parceiras', custo: 250 }
    ],
    Platina: [
      { nome: 'Fim de Semana em Resort', tipo: 'experiencia', descricao: 'Fim de semana em resort 4 estrelas', custo: 800 },
      { nome: 'Eletrônico Premium', tipo: 'fisico', descricao: 'Smartphone ou tablet de última geração', custo: 1500 }
    ],
    Diamante: [
      { nome: 'Viagem Nacional', tipo: 'experiencia', descricao: 'Pacote de viagem nacional completo', custo: 3000 },
      { nome: 'Bônus Financeiro', tipo: 'financeiro', descricao: 'Bônus em dinheiro de R$ 5.000', custo: 5000 }
    ]
  };

  niveis.forEach((nivel) => {
    const recompensasNivel = recompensasPorNivel[nivel.nivel];
    recompensasNivel.forEach((r) => {
      recompensas.push({
        id: `recomp-${idCounter++}`,
        nome: r.nome,
        tipo: r.tipo,
        descricao: r.descricao,
        custoEstimado: r.custo,
        nivel: nivel.nivel,
        frequencia: nivel.nivel === 'Bronze' || nivel.nivel === 'Prata' ? 'recorrente' : 'unica'
      });
    });
  });

  return recompensas;
};

export const calcularComplexidade = (campanha: Partial<CampanhaSimulada>) => {
  const totalMetas = campanha.metas?.length || 0;
  const totalDistintivos = campanha.distintivos?.length || 0;
  const totalMissoes = campanha.missoes?.length || 0;
  const totalRecompensas = campanha.recompensas?.length || 0;

  const pontuacao = (totalMetas * 2) + (totalDistintivos * 3) + (totalMissoes * 2) + (totalRecompensas * 1);
  const indiceComplexidade = Math.min(100, pontuacao);

  let nivelComplexidade: 'Simples' | 'Moderada' | 'Complexa' | 'Muito Complexa';
  if (indiceComplexidade < 25) nivelComplexidade = 'Simples';
  else if (indiceComplexidade < 50) nivelComplexidade = 'Moderada';
  else if (indiceComplexidade < 75) nivelComplexidade = 'Complexa';
  else nivelComplexidade = 'Muito Complexa';

  return {
    indiceComplexidade,
    totalMetas,
    totalDistintivos,
    totalMissoes,
    totalRecompensas,
    nivelComplexidade
  };
};

export const calcularCustos = (
  recompensas: RecompensaSelecionada[],
  participantes: ParticipanteSimulador[],
  produtos: ProdutoServicoSimulador[]
) => {
  const custoTotalRecompensas = recompensas.reduce((acc, r) => acc + r.custoEstimado, 0);
  
  const custoPorNivel: Record<NivelCampanha, number> = {
    Bronze: 0,
    Prata: 0,
    Ouro: 0,
    Platina: 0,
    Diamante: 0
  };

  recompensas.forEach((r) => {
    custoPorNivel[r.nivel] += r.custoEstimado;
  });

  const custoMedioPorParticipante = participantes.length > 0 
    ? custoTotalRecompensas / participantes.length 
    : 0;

  const custoPorPesoComercial: Record<string, number> = {
    Baixo: 0,
    Médio: 0,
    Alto: 0,
    Estratégico: 0
  };

  produtos.forEach((p) => {
    const incentivo = obterIncentivoPorPesoPerfil(p.pesoComercial, p.perfilEconomico);
    custoPorPesoComercial[p.pesoComercial] += incentivo.pontosMax * 0.1; // Estimativa
  });

  const distribuicaoCustos = recompensas.reduce((acc, r) => {
    const existing = acc.find(d => d.tipo === r.tipo);
    if (existing) {
      existing.valor += r.custoEstimado;
    } else {
      acc.push({ tipo: r.tipo, valor: r.custoEstimado, percentual: 0 });
    }
    return acc;
  }, [] as Array<{ tipo: string; valor: number; percentual: number }>);

  distribuicaoCustos.forEach((d) => {
    d.percentual = (d.valor / custoTotalRecompensas) * 100;
  });

  return {
    custoTotalRecompensas,
    custoPorNivel,
    custoMedioPorParticipante,
    custoPorPesoComercial,
    distribuicaoCustos
  };
};

export const gerarCampanhaCompleta = (
  dadosBasicos: { nome: string; descricao: string; tipo: TipoCampanha; dataInicio: string; dataFim: string },
  produtos: ProdutoServicoSimulador[],
  participantes: ParticipanteSimulador[],
  configIA?: ConfiguracaoIA & { gerarMetas: boolean; gerarDistintivos: boolean; gerarProgressao: boolean }
): CampanhaSimulada => {
  // Usar configuração padrão se não fornecida
  const config = configIA || { gerarMetas: true, gerarDistintivos: true, gerarProgressao: true };

  const metas = config.gerarMetas ? gerarMetasAutomaticas(produtos, dadosBasicos.tipo, config) : [];
  const distintivos = config.gerarDistintivos ? gerarDistintivosAutomaticos(produtos, config) : [];
  const missoes = config.gerarProgressao ? gerarMissoesAutomaticas(produtos, config) : [];
  const niveis = config.gerarProgressao ? gerarNiveisProgressao(produtos, config) : [];
  const recompensas = selecionarRecompensasAutomaticas(produtos, niveis);

  // Associar recompensas aos níveis
  niveis.forEach((nivel) => {
    nivel.recompensas = recompensas.filter(r => r.nivel === nivel.nivel);
  });

  const analiseComplexidade = calcularComplexidade({ metas, distintivos, missoes, recompensas });
  const analiseCustos = calcularCustos(recompensas, participantes, produtos);

  const campanha: CampanhaSimulada = {
    id: `camp-${Date.now()}`,
    nome: dadosBasicos.nome,
    descricao: dadosBasicos.descricao,
    tipo: dadosBasicos.tipo,
    dataInicio: dadosBasicos.dataInicio,
    dataFim: dadosBasicos.dataFim,
    produtosServicos: produtos,
    participantes,
    metas,
    distintivos,
    missoes,
    niveis,
    recompensas,
    analiseComplexidade,
    analiseCustos,
    sugestoesEvolutivas: [],
    geradoPorIA: true,
    dataSimulacao: new Date().toISOString(),
    status: 'rascunho'
  };

  // NOVO: Adiciona análise completa
  const analiseCompleta = analisarCampanhaCompleta(campanha);
  campanha.sugestoesEvolutivas = analiseCompleta.sugestoes;

  // NOVO: Adiciona ao histórico para aprendizado
  adicionarCampanhaAoHistorico(campanha);

  return campanha;
};
